import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Store, ImagePlus, X, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const ShopManage = () => {
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", tagline: "", bio: "", location: "", whatsapp: "", instagram: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api
      .get("/shops/me")
      .then((r) => {
        const s = r.data;
        setShop(s);
        setForm({
          name: s.name || "", tagline: s.tagline || "", bio: s.bio || "",
          location: s.location || "", whatsapp: s.whatsapp || "", instagram: s.instagram || "",
        });
        setLogoUrl(s.logo_url || "");
        setBannerUrl(s.banner_url || "");
        setIsOpen(!!s.is_open);
      })
      .catch(() => toast.error("Could not load your shop"))
      .finally(() => setLoading(false));
  }, []);

  const upload = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data?.url;
      if (!url) throw new Error("no url");
      kind === "logo" ? setLogoUrl(url) : setBannerUrl(url);
    } catch {
      toast.error("Upload failed — try a smaller image");
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/shops/me", {
        name: form.name.trim(),
        tagline: form.tagline.trim(),
        bio: form.bio.trim(),
        location: form.location.trim(),
        whatsapp: form.whatsapp.trim(),
        instagram: form.instagram.trim(),
        logo_url: logoUrl || null,
        banner_url: bannerUrl || null,
        is_open: isOpen,
      });
      setShop(data);
      toast.success(isOpen ? "Shop saved & open" : "Shop saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>;
  }

  const inputCls = "mt-1";
  const labelCls = "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-24" data-testid="shop-manage-page">
      <div className="container-narrow max-w-2xl py-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold text-[#C05A3C] mb-1">
              <Store className="w-3.5 h-3.5" /> My shop
            </div>
            <h1 className="font-display text-3xl tracking-tight">Customise your shop</h1>
          </div>
          {shop?.slug && (
            <Link to={`/shop/${shop.slug}`} className="text-sm text-[#C05A3C] hover:underline inline-flex items-center gap-1" data-testid="shop-view-link">
              View shop <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        {/* Open / closed toggle — the shop is optional */}
        <div className="mt-6 rounded-xl border border-black/10 bg-white p-4 flex items-start justify-between gap-4" data-testid="shop-open-toggle-row">
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">
              <Store className="w-4 h-4 text-[#C05A3C]" /> Open my shop
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              When open, your shop appears in the Shops directory and buyers can follow it. Keep it
              off to just sell normally — your products still show in Browse either way.
            </p>
          </div>
          <Switch checked={isOpen} onCheckedChange={setIsOpen} data-testid="shop-open-switch" />
        </div>

        {/* Banner */}
        <div className="mt-6">
          <label className={labelCls}>Cover banner</label>
          <div className="mt-2 h-32 rounded-xl bg-gradient-to-br from-[#C05A3C]/20 to-[#2A3D36]/10 relative overflow-hidden border border-black/10">
            {bannerUrl && <img src={bannerUrl} alt="" className="w-full h-full object-cover" />}
            <label className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/0 hover:bg-black/10 transition-colors">
              <span className="px-3 py-1.5 rounded-full bg-white/90 text-sm font-medium inline-flex items-center gap-1.5">
                {uploading === "banner" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload banner
              </span>
              <input type="file" accept="image/*" onChange={(e) => upload(e, "banner")} className="hidden" data-testid="shop-banner-input" />
            </label>
            {bannerUrl && (
              <button onClick={() => setBannerUrl("")} className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70" data-testid="shop-banner-remove">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Logo + name */}
        <div className="mt-4 flex items-end gap-4">
          <div>
            <label className={labelCls}>Logo</label>
            <div className="mt-1 w-20 h-20 rounded-xl bg-[#1A2024] overflow-hidden relative flex items-center justify-center">
              {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" /> : <Store className="w-7 h-7 text-white/50" />}
              <label className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/0 hover:bg-black/30 transition-colors">
                {uploading === "logo" ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <ImagePlus className="w-4 h-4 text-white/0 hover:text-white" />}
                <input type="file" accept="image/*" onChange={(e) => upload(e, "logo")} className="hidden" data-testid="shop-logo-input" />
              </label>
            </div>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Shop name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shop-input-name" className={inputCls} placeholder="e.g. Afro Styles UK" />
          </div>
        </div>

        <div className="mt-4">
          <label className={labelCls}>Tagline</label>
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="shop-input-tagline" className={inputCls} placeholder="Authentic African fashion, shipped worldwide" />
        </div>

        <div className="mt-4">
          <label className={labelCls}>About your shop</label>
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            data-testid="shop-input-bio"
            rows={4}
            className="mt-1 w-full rounded-md border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30 resize-y"
            placeholder="Tell buyers about your shop, your story, and what makes your products special."
          />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Location</label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="shop-input-location" className={inputCls} placeholder="London, UK" />
          </div>
          <div>
            <label className={labelCls}>WhatsApp</label>
            <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="shop-input-whatsapp" className={inputCls} placeholder="+44…" />
          </div>
          <div>
            <label className={labelCls}>Instagram</label>
            <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} data-testid="shop-input-instagram" className={inputCls} placeholder="@yourshop" />
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="shop-save-btn" className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-full px-8 h-11">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save shop"}
          </Button>
          <Link to="/dashboard/my-listings" className="text-sm text-muted-foreground hover:text-[#C05A3C]">Add products to your shop →</Link>
        </div>
      </div>
    </div>
  );
};

export default ShopManage;
